import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { defaultIfEmpty, switchMap } from 'rxjs/operators';
import { Acl } from 'src/app/models/acl';
import { EnumAffinities } from 'src/app/models/enum-affinities';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { ExternalResponse } from 'src/app/models/external-response';
import { Property } from 'src/app/models/property';
import { User } from 'src/app/models/user';
import { ExternalsParametersService } from 'src/app/services/externals-parameters.service';
import { UsersService } from 'src/app/services/users.service';
import { MatButton } from '@angular/material/button';
import { RightsComponent } from '../rights/rights.component';
import { PropertiesComponent } from '../properties/properties.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    imports: [
        ReactiveFormsModule,
        MatTabGroup,
        MatTab,
        MatCard,
        MatCardContent,
        MatFormField,
        MatLabel,
        MatInput,
        MatIcon,
        MatSuffix,
        MatTooltip,
        PropertiesComponent,
        RightsComponent,
        MatButton,
        TranslateModule,
    ],
})
export class ProfileComponent implements OnInit, OnDestroy {
    profileForm: UntypedFormGroup;
    userProfile: User;
    userProperties: Property[] = [];
    userRights: Acl[] = [];
    affinity = EnumAffinities.User;

    isExternal = false;
    externalParameter: ExternalParameters;
    externalParametersChange: Subscription;

    constructor(
        private formbuilder: UntypedFormBuilder,
        private usersService: UsersService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private spinner: NgxSpinnerService,
        private externalsParametersService: ExternalsParametersService
    ) {}

    ngOnDestroy(): void {
        this.externalParametersChange.unsubscribe();
    }

    ngOnInit() {
        this.loadDatas();

        this.externalParametersChange = this.externalsParametersService.externalParametersChange$.subscribe((parameters) => {
            if (parameters) {
                this.isExternal = parameters ? true : false;
                this.externalParameter = parameters;
            }
        });

        // Pour changer le format des dates en cas de changement de langue
        this.translate.onLangChange.subscribe(() => {
            if (this.userProfile) {
                this.profileForm.get('generalGroup').get('createdAt').setValue(new DatePipe(this.translate.currentLang).transform(this.userProfile.createdAt, 'short'));
                this.profileForm.get('generalGroup').get('updatedAt').setValue(new DatePipe(this.translate.currentLang).transform(this.userProfile.updatedAt, 'short'));
                this.profileForm
                    .get('generalGroup')
                    .get('organizationAccessLevel')
                    .setValue(this.translate.instant('EnumOrganizationAccessLevels.' + this.userProfile.organizationAccessLevel));
                this.profileForm
                    .get('generalGroup')
                    .get('userAccessLevel')
                    .setValue(this.translate.instant('EnumUserAccessLevels.' + this.userProfile.organizationAccessLevel));
            }
        });
    }

    loadDatas() {
        this.spinner.show();

        this.userProfile = null;

        // Récupère les informations de l'utilisateur connecté
        this.usersService
            .getSessionCurrentUser()
            .pipe(
                // Recharge les informations
                switchMap((user) => this.usersService.getUserWithDisplayParentOrganization(user.id, false))
            )
            .subscribe((currentUser) => {
                this.userProfile = currentUser;
                this.userProperties = Array.from(this.userProfile.properties);
                this.userRights = this.userProfile.rights;

                // Créé le formulaire
                this.profileForm = this.formbuilder.group({
                    generalGroup: this.formbuilder.group({
                        display: [this.userProfile.display],
                        account: [this.userProfile.account],
                        networks: [this.userProfile.networks],
                        enabled: [this.userProfile.enabled],
                        billing: [this.userProfile.billing],
                        appData: [this.userProfile.appData],
                        createdAt: [new DatePipe(this.translate.currentLang).transform(this.userProfile.createdAt, 'short')],
                        updatedAt: [new DatePipe(this.translate.currentLang).transform(this.userProfile.updatedAt, 'short')],
                        deletedAt: [this.userProfile.deletedAt],
                        displayParentOrganization: [this.userProfile.displayParentOrganization],
                        organizationAccessLevel: [this.translate.instant('EnumOrganizationAccessLevels.' + this.userProfile.organizationAccessLevel)],
                        userAccessLevel: [this.translate.instant('EnumUserAccessLevels.' + this.userProfile.userAccessLevel)],
                    }),
                });
                this.spinner.hide();
            });
    }

    onSubmit() {
        this.spinner.show();

        const requests: Observable<any>[] = this.getPropertiesAndRightsRequests(this.userProfile.id);

        forkJoin(requests)
            .pipe(defaultIfEmpty(null))
            .subscribe({
                next: () => {
                    if (this.isExternal) {
                        const response = new ExternalResponse();
                        response.code = 200;
                        response.message = encodeURI(this.translate.instant('Profile.SuccessMessageUpdateProfile'));
                        window.location.href = `${this.externalParameter.return}${this.externalParameter.return.includes('?') ? '&' : '?'}response=${JSON.stringify(response)}`;
                    } else {
                        this.toastr.success(this.translate.instant('Profile.SuccessMessageUpdateProfile'));
                        this.loadDatas();
                    }
                },
                error: (error: unknown) => {
                    if (this.isExternal) {
                        const httpError = error as HttpErrorResponse;
                        const response = new ExternalResponse();
                        response.code = httpError.status;
                        response.message = encodeURI(this.translate.instant('Profile.ErrorMessageUpdateProfile'));
                        window.location.href = `${this.externalParameter.return}${this.externalParameter.return.includes('?') ? '&' : '?'}response=${JSON.stringify(response)}`;
                    } else {
                        this.toastr.warning(this.translate.instant('Profile.ErrorMessageUpdateProfile'));
                        this.loadDatas();
                    }
                },
            });
    }

    /** Lors du changement des propriétés */
    onPropertiesChanged(profileProperties: Property[]) {
        this.userProperties = profileProperties;
    }

    /** Lors du changement des droits */
    onRightsChanged(profileRights: Acl[]) {
        this.userRights = profileRights;
    }

    getPropertiesAndRightsRequests(userId: string): any[] {
        let requests: any[] = [];

        // Recherche des propriétés à créer ou mettre à jour
        this.userProperties.forEach((p) => {
            const userProperty = this.userProfile.properties.filter((up) => up.name === p.name);
            if (userProperty.length > 0) {
                // Vérifie si la valeur a changée
                if (userProperty[0].value !== p.value) {
                    requests.push(this.usersService.updateUserProperty(userId, p.name, p.value));
                }
            } else {
                // N'existe pas, à ajouter
                requests.push(this.usersService.updateUserProperty(userId, p.name, p.value));
            }
        });

        // Recherche des propriétés à supprimer
        this.userProfile.properties.forEach((p) => {
            if (!this.userProperties.find((up) => up.name === p.name)) {
                // A supprimer
                requests.push(this.usersService.deleteUserProperty(userId, p.name));
            }
        });

        return requests;
    }

    cancelButtonClick() {
        if (this.isExternal) {
            window.location.href = this.externalParameter.return;
        } else {
            this.loadDatas();
        }
    }
}
